import {
  setupReturnCorrelationSchema,
  readCorrelatedSetupReturn,
  clearCorrelatedSetupReturn,
} from "components/payment/PaymentMethodSetup/setupReturn";
import * as yup from "yup";

export type PaymentLinkSetupContext = (
  | { surface: "payment_link"; requestId: string }
  | { surface: "invoice"; invoiceId: string }
) & { payerId?: string };

const STORAGE_KEY = "moovs.paymentLinkMethodSetup";
const correlationSchema = setupReturnCorrelationSchema.shape({
  operatorId: yup.string().required(),
  surface: yup.string().oneOf(["payment_link", "invoice"]).required(),
  sourceId: yup.string().required(),
  payerId: yup.string().optional(),
  authenticatedContactId: yup.string().optional(),
});

type Correlation = yup.InferType<typeof correlationSchema>;
export type PaymentLinkSetupReturn = Correlation & { clientSecret: string };

export const matchesPaymentLinkSetup = (
  correlation: Correlation,
  operatorId: string,
  context: PaymentLinkSetupContext,
  pathname: string,
  authenticatedContactId?: string
) =>
  correlation.operatorId === operatorId &&
  correlation.surface === context.surface &&
  correlation.sourceId ===
    (context.surface === "invoice" ? context.invoiceId : context.requestId) &&
  correlation.returnPath === pathname &&
  // Older returns used the source payer, regardless of subsequent login.
  (!correlation.payerId ||
    (correlation.authenticatedContactId === authenticatedContactId &&
      (!context.payerId || correlation.payerId === context.payerId)));

export const retainPaymentLinkSetup = (
  operatorId: string,
  context: PaymentLinkSetupContext,
  returnPath: string,
  setupIntentId: string,
  authenticatedContactId?: string
) => {
  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      operatorId,
      surface: context.surface,
      sourceId:
        context.surface === "invoice" ? context.invoiceId : context.requestId,
      returnPath,
      payerId: context.payerId,
      authenticatedContactId,
      setupIntentId,
    })
  );
};

export const readPaymentLinkSetupReturn = (
  operatorId: string,
  context: PaymentLinkSetupContext,
  pathname: string,
  search: string,
  authenticatedContactId?: string
): PaymentLinkSetupReturn | null => {
  const returned = readCorrelatedSetupReturn(
    STORAGE_KEY, correlationSchema, pathname, search
  );
  return returned &&
    matchesPaymentLinkSetup(
      returned, operatorId, context, pathname, authenticatedContactId
    )
    ? returned
    : null;
};

export const clearPaymentLinkSetup = (setupIntentId: string) =>
  clearCorrelatedSetupReturn(STORAGE_KEY, correlationSchema, setupIntentId);
